// Contacts CSV upload modal. Restores the pre-sales-platform upload UX:
// - Drag/drop + browse + "Download Template" link (opens a popup with
//   required-columns cheat sheet + a one-click template CSV).
// - Inline validation errors grouped per row.
// - Fix errors IN PLACE: ErrorRowEditor (1-10 errors) or ErrorEditorModal
//   (11-50 errors). > 50 forces a re-upload.
//
// The upload UI components (NewRunLeadsUpload, NewRunTemplatePopup,
// ErrorRowEditor, ErrorEditorModal) are reused verbatim from the
// pre-LT-182 /new-run wizard — same files, same styling. Only the
// state wiring moved here so Contacts can host the flow without the
// wizard's unrelated agent / credits / scheduling state.
//
// On submit we serialise the validated Leads (name, phone, email) to
// a CSV the existing POST /contacts/bulk-upload accepts. `name` is
// split into first_name/last_name server-side — we send it pre-split
// here so rows render correctly in the contacts table on the first
// render.
// LT-310: DS v1 migration — <Button>, <IconButton>, token-based banners.

import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

import NewRunLeadsUpload from '../NewRunPage/NewRunLeadsUpload';
import NewRunTemplatePopup from '../NewRunPage/NewRunTemplatePopup';
import type { Lead } from '../NewRunPage/newRunTypes';
import { contactsAPI } from '../../services/contactsAPI';
import { contactListsAPI } from '../../services/contactListsAPI';
import type { ContactList } from '../../types/contactList';
import { useCsvLeadsUpload } from './useCsvLeadsUpload';
import { Button, IconButton, Input, Radio, Select } from '../../ui';

export interface ContactsUploadResult {
  created: number;
  /** Set when the batch was dropped into a static list (new or existing). */
  listId?: string | null;
  listName?: string | null;
  /** Ids of the contacts actually created by this upload. */
  createdContactIds?: string[];
}

interface ContactsUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploaded: (result: ContactsUploadResult) => void;
}

function extractUploadErr(e: unknown): string {
  const ax = e as { response?: { data?: { detail?: { message?: string } | string } } };
  const d = ax?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && d.message) return d.message;
  return e instanceof Error ? e.message : 'Upload failed';
}

function splitName(raw: string): { first_name: string; last_name: string } {
  const parts = raw.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
}

function leadsToCsvFile(leads: Lead[]): File {
  const header = 'first_name,last_name,phone,email\n';
  const body = leads
    .map((l) => {
      const { first_name, last_name } = splitName(l.name || '');
      // Minimal CSV escaping: wrap in quotes only if the value contains
      // a comma or quote; double any embedded quote.
      const esc = (v: string) =>
        v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
      return [
        esc(first_name),
        esc(last_name),
        esc(l.phone || ''),
        esc(l.email || ''),
      ].join(',');
    })
    .join('\n');
  const blob = new Blob([header + body + '\n'], { type: 'text/csv;charset=utf-8;' });
  return new File([blob], 'contacts_upload.csv', { type: 'text/csv' });
}

const ContactsUploadModal: React.FC<ContactsUploadModalProps> = ({
  isOpen,
  onClose,
  onUploaded,
}) => {
  const {
    fileInputRef,
    selectedFile,
    leads,
    validationErrors,
    previewMode,
    toast,
    showTemplatePopup,
    setPreviewMode,
    setShowTemplatePopup,
    setToast,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleClearFile,
    handleSaveCorrections,
    exportValidationErrors,
    downloadTemplate,
    reset,
  } = useCsvLeadsUpload();

  const [submitting, setSubmitting] = useState(false);
  const [listMode, setListMode] = useState<'none' | 'new' | 'existing'>('none');
  const [newListName, setNewListName] = useState('');
  const [existingListId, setExistingListId] = useState('');
  const [lists, setLists] = useState<ContactList[]>([]);
  const [listsLoading, setListsLoading] = useState(false);

  // Load the workspace's static lists for the "existing list" picker
  // whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setListsLoading(true);
    contactListsAPI
      .list({ limit: 200 })
      .then((rows) => setLists(rows.filter((l) => l.list_type === 'static')))
      .catch(() => setLists([]))
      .finally(() => setListsLoading(false));
  }, [isOpen]);

  const resetLocal = () => {
    setListMode('none');
    setNewListName('');
    setExistingListId('');
  };

  if (!isOpen) return null;

  const listSelectionReady =
    listMode === 'none' ||
    (listMode === 'new' && newListName.trim().length > 0) ||
    (listMode === 'existing' && existingListId.length > 0);

  const canSubmit =
    leads.length > 0 &&
    validationErrors.length === 0 &&
    !submitting &&
    listSelectionReady;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setToast(null);
    try {
      const file = leadsToCsvFile(leads);
      const opts =
        listMode === 'new'
          ? { newListName: newListName.trim() }
          : listMode === 'existing'
            ? { listId: existingListId }
            : undefined;
      const resp = await contactsAPI.bulkUpload(file, opts);
      const listName =
        listMode === 'new'
          ? newListName.trim()
          : listMode === 'existing'
            ? lists.find((l) => l.id === existingListId)?.name ?? null
            : null;
      onUploaded({
        created: resp.created ?? leads.length,
        listId: resp.list_id ?? null,
        listName,
        createdContactIds: resp.created_contact_ids ?? [],
      });
      resetLocal();
      reset();
      onClose();
    } catch (e) {
      setToast({ type: 'error', message: extractUploadErr(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    resetLocal();
    reset();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 backdrop-blur-sm p-4">
      <div
        className="relative w-full max-w-4xl my-8 bg-surface-1 border border-white/[0.08] rounded-ds-xl shadow-ds-e3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b border-white/[0.08] bg-surface-1/95 backdrop-blur">
          <h2 className="text-base font-semibold text-slate-50">
            Upload contacts from CSV
          </h2>
          <IconButton
            intent="ghost"
            size="sm"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Close"
          >
            <XMarkIcon className="h-4 w-4" />
          </IconButton>
        </div>

        <div className="px-5 py-4 space-y-4">
          <NewRunLeadsUpload
            fileInputRef={fileInputRef}
            selectedFile={selectedFile}
            leads={leads}
            validationErrors={validationErrors}
            previewMode={previewMode}
            onFileSelect={handleFileSelect}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onShowTemplate={() => setShowTemplatePopup(true)}
            onExportErrors={exportValidationErrors}
            onTogglePreview={() => setPreviewMode(!previewMode)}
            onClearFile={handleClearFile}
            onSaveCorrections={handleSaveCorrections}
          />

          <div className="rounded-ds-lg border border-white/[0.08] bg-surface-2 p-3 space-y-3">
            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
              Add to a list (optional)
            </div>
            <div className="flex flex-col gap-2">
              <Radio
                name="upload-list-mode"
                checked={listMode === 'none'}
                onChange={() => setListMode('none')}
                disabled={submitting}
                label="Don't add to a list"
              />
              <Radio
                name="upload-list-mode"
                checked={listMode === 'new'}
                onChange={() => setListMode('new')}
                disabled={submitting}
                label="Create a new list from this upload"
              />
              <Radio
                name="upload-list-mode"
                checked={listMode === 'existing'}
                onChange={() => setListMode('existing')}
                disabled={submitting}
                label="Add to an existing list"
              />
            </div>
            {listMode === 'new' ? (
              <Input
                label="New list name"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="e.g. NCR developers — June batch"
                disabled={submitting}
                maxLength={255}
              />
            ) : null}
            {listMode === 'existing' ? (
              <div>
                <Select
                  label="List"
                  value={existingListId}
                  onChange={(e) => setExistingListId(e.target.value)}
                  disabled={submitting || listsLoading}
                >
                  <option value="">
                    {listsLoading ? 'Loading…' : '— Select a list —'}
                  </option>
                  {lists.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </Select>
                {!listsLoading && lists.length === 0 ? (
                  <p className="text-xs text-slate-500 mt-1">
                    No static lists yet. Create a new one instead.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          {toast && (
            <div
              className={`text-sm rounded-ds-lg px-3 py-2 border ${
                toast.type === 'success'
                  ? 'text-ok-text border-ok-border bg-ok-tint'
                  : 'text-danger-text border-danger-border bg-danger-tint'
              }`}
            >
              {toast.message}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.08] bg-surface-1/95 backdrop-blur">
          <div className="text-xs text-slate-400">
            {leads.length > 0 && validationErrors.length === 0 && (
              <span className="text-ok-text">
                {leads.length} contact{leads.length === 1 ? '' : 's'} ready to upload
              </span>
            )}
            {validationErrors.length > 0 && (
              <span className="text-danger-text">
                {validationErrors.length} error
                {validationErrors.length === 1 ? '' : 's'} to fix before uploading
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button intent="ghost" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button intent="primary" onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? 'Uploading…' : `Upload ${leads.length || ''} contact${leads.length === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>

        {showTemplatePopup && (
          <NewRunTemplatePopup
            onDownload={downloadTemplate}
            onClose={() => setShowTemplatePopup(false)}
          />
        )}
      </div>
    </div>
  );
};

export default ContactsUploadModal;
